import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import usePageMeta from "../lib/usePageMeta";

export default function AdminLogin() {
  usePageMeta({ title: "Admin Login · AARA", noindex: true });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const denied = params.get("denied");
  const error = params.get("error");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // If returning from OAuth callback, skip auth check
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.is_admin) navigate("/admin");
      } catch (err) {
        // Not authenticated — expected on first visit. Log for debugging only.
        if (err?.response?.status !== 401) {
          console.error("AdminLogin auth check failed:", err);
        }
      }
      setChecking(false);
    })();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F7F5F0]">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-6 grain-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="text-sm text-[#64748B] hover:text-[#0F172A]" data-testid="back-home-link">← Back to site</Link>
        <div className="mt-6 bg-white rounded-2xl border border-[#E2DDD3] shadow-sm p-10">
          <div className="text-center mb-8">
            <div className="inline-block w-20 h-20 rounded-2xl mb-4 overflow-hidden ring-1 ring-[#E2DDD3] shadow-sm bg-white grid place-items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/iyaa4h8h_American%20Asset%20logo-01.png"
                alt="American Asset Recovery Agency"
                className="w-full h-full object-contain p-1.5"
              />
            </div>
            <h1 className="font-serif-display text-3xl text-[#0F172A]">Admin Access</h1>
            <p className="mt-2 text-sm text-[#64748B]">American Asset Recovery Agency</p>
          </div>

          {denied && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm" data-testid="login-denied-msg">
              This Google account is not authorized for admin access.
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm" data-testid="login-error-msg">
              Login failed. Please try again.
            </div>
          )}

          <Button
            onClick={handleLogin}
            data-testid="google-login-button"
            className="w-full h-12 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white rounded-xl transition-all duration-300 hover:-translate-y-0.5"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2"><path fill="#FFFFFF" d="M21.35 11.1H12v3.2h5.35c-.23 1.4-1.7 4.1-5.35 4.1-3.2 0-5.8-2.65-5.8-5.9s2.6-5.9 5.8-5.9c1.83 0 3.05.78 3.75 1.45l2.55-2.45C16.85 4.05 14.65 3 12 3 6.95 3 2.85 7.1 2.85 12s4.1 9 9.15 9c5.3 0 8.8-3.7 8.8-8.9 0-.6-.07-1.05-.15-1.5z"/></svg>
            Continue with Google
          </Button>

          <p className="mt-6 text-xs text-center text-[#64748B]">
            Only authorized email addresses can access the admin dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}
